import React, { useMemo } from 'react';
import { Box, useTheme, useMediaQuery, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { YearData, MetricType, PeriodType } from '../../../store/modules/insights/types';
import {
  transformDataForChart,
  formatChartValue,
  getMetricDisplayName,
  getQuarterMonthLabel,
} from '../../../util/insightsUtils';

interface MetricsChartProps {
  data?: YearData;
  metricType: MetricType;
  periodType: PeriodType;
  isLoading?: boolean;
}

export const MetricsChart: React.FC<MetricsChartProps> = ({
  data,
  metricType,
  periodType,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const chartData = useMemo(() => {
    if (!data) return [];
    return transformDataForChart(data, metricType, periodType);
  }, [data, metricType, periodType]);

  const emptyStateBox = (message: string) => (
    <Box
      sx={{
        width: '100%',
        height: isMobile ? '300px' : '472px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'secondary.main',
        backgroundColor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: '32px',
      }}
    >
      <Typography variant="medium" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  if (isLoading) {
    return emptyStateBox('Loading chart...');
  }

  if (!data || chartData.length === 0) {
    return emptyStateBox('No data available for this period');
  }

  const xAxisData = chartData.map((item) => item.period);
  const seriesData = chartData.map((item) => item.value);
  const metricDisplayName = getMetricDisplayName(metricType);

  const barColor =
    metricType === 'refundGmv' || metricType === 'refundTransactionCount'
      ? theme.palette.error.main
      : theme.palette.brand.dark;

  const valueFormatter = (value: number | null) => {
    if (value === null || value === 0) return '0';

    if (metricType === 'authorizationRate') {
      return `${value}%`;
    }

    // For better readability, use more conservative thresholds to avoid duplicate labels
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }

    // Only use K notation for values >= 10K to avoid confusion with smaller values
    if (value >= 10000) {
      return `${(value / 1000).toFixed(0)}K`;
    }

    // For values 1000-9999, use decimal K notation for clarity
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }

    return value.toString();
  };

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'secondary.main',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '10px 10px 0px 0px' : '20px 32px',
        minWidth: '260px',
        minHeight: '130px',
      }}
    >
      <BarChart
        xAxis={[
          {
            scaleType: 'band',
            data: xAxisData,
            tickLabelStyle: {
              fontSize: isMobile ? 8 : 10,
              fill: theme.palette.text.primary,
            },
            valueFormatter: (period) =>
              getQuarterMonthLabel(period) ? `${period}\n${getQuarterMonthLabel(period)}` : period,
            height: 40,
            tickPlacement: 'extremities',
            tickLabelPlacement: 'middle',
            categoryGapRatio: 0.3,
            barGapRatio: 0,
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: {
              fontSize: 12,
              fill: theme.palette.text.primary,
            },
            valueFormatter,
            width: !isMobile ? 75 : 45,
            label: !isMobile ? metricDisplayName : undefined,
          },
        ]}
        series={[
          {
            data: seriesData,
            color: barColor,
            valueFormatter: (value) => formatChartValue(value, metricType),
          },
        ]}
        width={undefined}
        height={isMobile ? 240 : 440}
        borderRadius={3}
        grid={{
          horizontal: true,
          vertical: false,
        }}
        sx={{
          '& .MuiChartsAxis-left .MuiChartsAxis-line': {
            stroke: 'text.primary',
            strokeWidth: 1,
          },
          '& .MuiChartsAxis-left .MuiChartsAxis-tick': {
            stroke: 'text.primary',
            strokeWidth: 1,
          },
          '& .MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
            fill: 'text.primary',
            fontSize: '12px',
          },
          '& .MuiChartsAxis-bottom .MuiChartsAxis-line': {
            stroke: 'text.primary',
            strokeWidth: 1,
          },
          '& .MuiChartsAxis-bottom .MuiChartsAxis-tick': {
            stroke: 'text.primary',
            strokeWidth: 1,
          },
          '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
            fill: 'text.primary',
            fontSize: '10px',
          },
          '& .MuiChartsGrid-line': {
            stroke: 'secondary.light',
            strokeWidth: 1,
            opacity: 0.6,
          },
          '& .MuiBarElement-root': {
            fill: barColor,
            rx: 3,
            ry: 3,
          },
          '& .MuiBarElement-root:hover': {
            opacity: 0.85,
            filter: 'brightness(1.05)',
          },
          '& .MuiChartsSurface-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiBarChart-root': {
            width: '100%',
            height: '100%',
          },
        }}
      />
    </Box>
  );
};
